import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { usePreloading } from '../../contexts/SiteContext';
import { logger } from '../../utils/logger';
import imagePreloader from '../../utils/imagePreloader';
import VintageButton from '../ui/VintageButton';
import { QUESTIONS } from '../../config/questionConfig';
import { updateModelPath } from '../../utils/modelPath';
import { createGLTFLoader } from '../../utils/gltfLoader';

// Global preload state to prevent duplicate preloading
let preloadState = {
  isPreloading: false,
  isComplete: false,
  promise: null
};

const Preloader = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [assetLoadingProgress, setAssetLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('Initializing...');
  const [showRevealAnimation, setShowRevealAnimation] = useState(false);
  const [showProceedButton, setShowProceedButton] = useState(false);
  const [showButtonAnimation, setShowButtonAnimation] = useState(false);
  // Button interactions are handled inside VintageButton
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [allMessagesShown, setAllMessagesShown] = useState(false);
  const { registerModel, getModel, setModelLoaded, createModelPromise, setReadyToPositionSignal, assetQuality } = useCanvasContext();
  const { setIsPreloading } = usePreloading();
  const hasStartedRef = useRef(false);
  const loadedCountRef = useRef(0);
  const animationFrameRef = useRef(null);
  // Removed external button container/ring; now encapsulated in VintageButton
  const preloaderRef = useRef(null);
  const debugPreloadRef = useRef(false);
  
  // Check for debug flag in URL (dev mode only)
  useEffect(() => {
    // Only enable debug mode in development
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (!isDevelopment) {
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const debugPreloadFlag = urlParams.get('debugPreload') === 'true';
    debugPreloadRef.current = debugPreloadFlag;
    if (debugPreloadFlag) {
      logger.debug('🔍 Debug mode: Preloader will be semi-transparent to show canvas');
    }
  }, []);

  // Removed ring/button animation from Preloader; now handled inside VintageButton
  
  // Animation duration constants - faster in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  const NORMAL_ANIMATION_DURATION = isDevelopment ? 300 : 3000; // 300ms in dev, 3s in production
  const FAST_ANIMATION_DURATION = isDevelopment ? 50 : 200; // 50ms in dev, 200ms in production

  // Array of New Work City messages in sequential order
  const nwcMessages = useMemo(() => [
    "Newcomer. Your reality will be reimagined shortly.",
    "Now entering New Work City.",
    "Please keep your imagination inside the vehicle.",
    "Wiping your browser history of boring ideas…",
    "Generating skyline of impossible possibilities…",
    "Cross-checking your vibe clearance with City Control…",
    "A permit for 'Radical Reimagination' has been approved. Proceed.",
    "Establishing neural link with local residents…",
    "Your creative passport has been stamped: NWC Citizen.",
    "Welcome to New Work City",
    "Warning: prolonged exposure may result in original thought."
  ], []);

  // Function to get the next message in sequence
  const getNextMessage = useCallback(() => {
    const nextIndex = (currentMessageIndex + 1) % nwcMessages.length;
    setCurrentMessageIndex(nextIndex);
    return nwcMessages[nextIndex];
  }, [currentMessageIndex, nwcMessages]);


  // Function to cycle through all messages during loading
  const cycleThroughMessages = useCallback(() => {
    let messageIndex = 0; // Start with 0 since first message is already shown
    let resolvePromise;
    let timeoutId;
    
    const messagePromise = new Promise((resolve) => {
      resolvePromise = resolve;
    });
    
    // Use setTimeout chaining for more precise timing
    const showNextMessage = () => {
      messageIndex++;
      const timestamp = new Date().toISOString();
      
      if (messageIndex < nwcMessages.length) {
        setCurrentMessageIndex(messageIndex);
        setLoadingStatus(nwcMessages[messageIndex]);
        logger.debug(`📝 [${timestamp}] Showing message ${messageIndex + 1}/${nwcMessages.length}: "${nwcMessages[messageIndex]}"`);
        
        // Schedule next message - faster in development mode
        const messageDelay = isDevelopment ? 200 : 1000; // 200ms in dev, 1s in production
        timeoutId = setTimeout(showNextMessage, messageDelay);
      } else {
        // All messages have been shown (including the last one)
        logger.debug(`✅ [${timestamp}] All ${nwcMessages.length} messages have been shown!`);
        setAllMessagesShown(true);
        resolvePromise();
      }
    };
    
    // Start the first message change - faster in development mode
    const initialDelay = isDevelopment ? 200 : 1000; // 200ms in dev, 1s in production
    timeoutId = setTimeout(showNextMessage, initialDelay);

    return { cleanup: () => clearTimeout(timeoutId), promise: messagePromise };
  }, [nwcMessages, isDevelopment]);

  // Calculate combined progress based on message progress and asset loading progress
  useEffect(() => {
    // Message progress: 0 to 50% based on how many messages have been shown
    // When currentMessageIndex reaches 10 (last message), messageProgress should be 50%
    const messageProgress = ((currentMessageIndex + 1) / nwcMessages.length) * 50; // Messages account for 50% of progress
    // Asset progress: 0 to 50% based on asset loading, but cap it to not exceed message progress
    const assetProgress = Math.min((assetLoadingProgress / 100) * 50, messageProgress); // Assets account for 50% of progress
    const combinedProgress = Math.min(messageProgress + assetProgress, 100);
    setLoadingProgress(combinedProgress);
  }, [currentMessageIndex, assetLoadingProgress, nwcMessages.length]);

  // Smooth progress animation - gradually update displayed progress to match actual progress
  useEffect(() => {
    const targetProgress = loadingProgress;
    const currentDisplayed = displayedProgress;
    
    if (Math.abs(targetProgress - currentDisplayed) < 0.1) {
      setDisplayedProgress(targetProgress);
      return;
    }
    
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Use faster animation when close to 100% to avoid long final stretch
    const isNearCompletion = targetProgress >= 95;
    const animationDuration = isNearCompletion ? FAST_ANIMATION_DURATION : NORMAL_ANIMATION_DURATION;
    const startTime = Date.now();
    const startProgress = currentDisplayed;
    const progressDiff = targetProgress - startProgress;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // Use ease-out cubic bezier for smooth deceleration
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const newDisplayedProgress = startProgress + (progressDiff * easedProgress);
      
      setDisplayedProgress(newDisplayedProgress);
      
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        animationFrameRef.current = null;
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    // Cleanup function to cancel animation on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [loadingProgress, displayedProgress, FAST_ANIMATION_DURATION, NORMAL_ANIMATION_DURATION]);

  useEffect(() => {
    // Prevent duplicate preloading
    if (hasStartedRef.current) {
      return;
    }

    const preloadAssets = async () => {
      // Check if preloading is already complete
      if (preloadState.isComplete) {
        setAssetLoadingProgress(100);
        // Preloader completed
        return;
      }

      // Check if preloading is already in progress
      if (preloadState.isPreloading && preloadState.promise) {
        try {
          await preloadState.promise;
          setAssetLoadingProgress(100);
        } catch (error) {
          console.error('Preloading failed:', error);
        }
        return;
      }

      // Start preloading
      preloadState.isPreloading = true;
      hasStartedRef.current = true;
      
      // Set initial message
      setLoadingStatus(nwcMessages[0]);
      const initialTimestamp = new Date().toISOString();
      logger.debug(`📝 [${initialTimestamp}] Showing initial message 1/${nwcMessages.length}: "${nwcMessages[0]}"`);
      
      // Start cycling through messages
      const { cleanup: cleanupMessages, promise: messagesPromise } = cycleThroughMessages();

      // Create promises for each model to be positioned BEFORE loading starts
      logger.scene('🎯 Creating model positioning promises...');
      const phoneboxPromise = createModelPromise('phonebox');
      const tvPromise = createModelPromise('tv');
      const newsPromise = createModelPromise('news');

      const loader = createGLTFLoader();
      const assets = [
        { path: updateModelPath('/models/phonebox.glb', assetQuality), name: 'phonebox-scene' },
        { path: updateModelPath('/models/tv.glb', assetQuality), name: 'tv-scene' },
        { path: updateModelPath('/models/news_stand.glb', assetQuality), name: 'news-scene' }
      ];
      
      logger.model(`Preloading models with quality: ${assetQuality}`);

      loadedCountRef.current = 0; // Reset the counter
      // Logo images are loaded by React components when needed (no preloading to avoid duplicate requests)
      // Total assets: 3 models + 1 phonebox audio + 6 question audios + 1 hangup audio + 1 newspaper thumbnail
      const totalAssets = assets.length + 1 + QUESTIONS.length + 1 + 1;

      const preloadPromise = (async () => {
        // Preload 3D models
        for (const asset of assets) {
          
          // Check if model is already loaded
          const existingModel = getModel(asset.name);
          if (existingModel && existingModel.loaded) {
            logger.debug(`✅ Preloaded: ${asset.name} (from cache)`);
            loadedCountRef.current++;
            const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
            setAssetLoadingProgress(progress);
            continue;
          }
          
          try {
            const gltf = await new Promise((resolve, reject) => {
              const currentLoadedCount = loadedCountRef.current; // Capture the current value
              loader.load(
                asset.path,
                resolve,
                (progress) => {
                  // Ensure we have valid progress values
                  if (progress.total > 0) {
                    const assetProgress = (progress.loaded / progress.total) * 100;
                    const baseProgress = (currentLoadedCount / totalAssets) * 100;
                    const currentAssetProgress = (assetProgress / totalAssets);
                    const totalProgress = Math.min(baseProgress + currentAssetProgress, 100);
                    setAssetLoadingProgress(totalProgress);
                  }
                },
                reject
              );
            });

          // Register the model in global context
          registerModel(asset.name, {
            gltf,
            scene: gltf.scene,
            name: asset.name,
            path: asset.path,
            loaded: true,
            timestamp: Date.now()
          });

          // Don't signal loaded yet - let individual scenes handle positioning

          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
          logger.model(`✅ Preloaded: ${asset.name}`);
        } catch (error) {
          console.error(`❌ Failed to preload ${asset.name}:`, error);
          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
        }
        }

        // Preload phonebox audio
        try {
          const audio = new Audio('/audio/phonebox.mp3');
          audio.preload = 'auto';
          
          await new Promise((resolve, reject) => {
            audio.addEventListener('canplaythrough', resolve);
            audio.addEventListener('error', reject);
            audio.load();
          });

          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
          logger.audio('✅ Preloaded: payphone audio');
        } catch (error) {
          console.error('❌ Failed to preload phonebox audio:', error);
          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
        }

        // Preload all question audio files
        for (const question of QUESTIONS) {
          try {
            const questionAudio = new Audio(question.audioFile);
            questionAudio.preload = 'auto';
            
            await new Promise((resolve, reject) => {
              questionAudio.addEventListener('canplaythrough', resolve);
              questionAudio.addEventListener('error', reject);
              questionAudio.load();
            });

            loadedCountRef.current++;
            const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
            setAssetLoadingProgress(progress);
            logger.audio(`✅ Preloaded: ${question.id} audio`);
          } catch (error) {
            console.error(`❌ Failed to preload ${question.id} audio:`, error);
            loadedCountRef.current++;
            const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
            setAssetLoadingProgress(progress);
          }
        }

        // Preload hangup audio
        try {
          const hangupAudio = new Audio('/audio/hangup.mp3');
          hangupAudio.preload = 'auto';
          
          await new Promise((resolve, reject) => {
            hangupAudio.addEventListener('canplaythrough', resolve);
            hangupAudio.addEventListener('error', reject);
            hangupAudio.load();
          });

          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
          logger.audio('✅ Preloaded: hangup audio');
        } catch (error) {
          console.error('❌ Failed to preload hangup audio:', error);
          loadedCountRef.current++;
          const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
          setAssetLoadingProgress(progress);
        }

        // Preload newspaper thumbnail (small version for 3D model)
        // Full image will be loaded lazily when modal opens
        try {
          await imagePreloader.preload('/images/thumbnails/newspaper.jpg');
          logger.debug('✅ Preloaded: newspaper thumbnail');
        } catch (error) {
          console.error('❌ Failed to preload newspaper thumbnail:', error);
          // Continue with preloading even if thumbnail fails
        }

        loadedCountRef.current++;
        const progress = Math.min((loadedCountRef.current / totalAssets) * 100, 100);
        setAssetLoadingProgress(progress);

        // Logo SVG images are loaded by React components when needed
        // No preloading needed - they're small SVGs that load quickly
        logger.debug('✅ Logo SVG images will be loaded by React components');

        setAssetLoadingProgress(95);
        
        // Signal that preloader is ready to complete - models can now position to final locations
        logger.scene('🎯 Signaling models to move to final positions...');
        setReadyToPositionSignal(true);
        
        // Wait for all models to be positioned and ready AND all messages to be shown
        logger.scene('⏳ Waiting for models to be positioned and messages to be shown...');
        await Promise.all([phoneboxPromise, tvPromise, newsPromise, messagesPromise]);
        logger.scene('✅ All models positioned and all messages shown!');
        
        // Clean up message cycling
        cleanupMessages();
        
        setAssetLoadingProgress(100);
      })();

      preloadState.promise = preloadPromise;
    };

    preloadAssets();
  }, [registerModel, getModel, setModelLoaded, setIsPreloading, createModelPromise, setReadyToPositionSignal, getNextMessage, nwcMessages, cycleThroughMessages, assetQuality]);

  // Show PROCEED button only when preloading is complete, all messages have been shown, AND visual progress reaches 100%
  useEffect(() => {
    if (assetLoadingProgress === 100 && allMessagesShown && displayedProgress >= 99.9 && !showProceedButton) {
      const buttonDelay = isDevelopment ? 50 : 500; // 50ms in dev, 500ms in production
      setTimeout(() => {
        setShowProceedButton(true);
        // Trigger button animation after a short delay - faster in development mode
        const buttonAnimationDelay = isDevelopment ? 20 : 100; // 20ms in dev, 100ms in production
        setTimeout(() => {
          setShowButtonAnimation(true);
        }, buttonAnimationDelay);
      }, buttonDelay);
    }
  }, [assetLoadingProgress, allMessagesShown, displayedProgress, showProceedButton, isDevelopment]);

  // Handle overflow to prevent scrollbars - apply from mount, not just during reveal
  useEffect(() => {
    // Store scroll position before fixing body
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    // Prevent scrollbars from the start (preloader covers full screen)
    // Apply immediately and synchronously to prevent any flash of scrollbars
    document.body.classList.add('preloader-active');
    document.documentElement.classList.add('preloader-active');
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.classList.add('preloader-active');
    }
    
    // Force immediate style application
    void document.body.offsetHeight;
    
    // Store scroll position in data attribute for restoration
    document.body.setAttribute('data-scroll-y', scrollY.toString());
    document.body.setAttribute('data-scroll-x', scrollX.toString());
    
    // Note: Cleanup is handled in handleProceedClick after animation completes
    // We don't clean up here because the component unmounts before animation finishes
  }, []); // Run once on mount

  // Handle PROCEED button click
  const handleProceedClick = useCallback(() => {
    logger.animation('🎬 Preloader reveal animation starting...');
    setShowRevealAnimation(true);
    
    // Wait for preloader animation to complete before starting menu/header animations
    // This prevents menu animation from causing scrollbars during preloader fade-out
    setTimeout(() => {
      logger.animation('🎭 Starting menu/header animations and logo fade-in...');
      setIsPreloading(false); // This will trigger menu/header animations and logo fade-in
    }, NORMAL_ANIMATION_DURATION); // Wait for full preloader animation to complete
    
    // Wait for the full transition to complete before unmounting
    // This ensures the CSS transition can play out fully
    setTimeout(() => {
      logger.animation('✅ Preloader reveal animation complete, cleaning up...');
      preloadState.isComplete = true;
      preloadState.isPreloading = false;
      preloadState.promise = null;
      
      // Remove overflow classes after animation completes
      // Use a longer delay to ensure transition is fully complete and browser has finished rendering
      setTimeout(() => {
        // Restore scroll position if it was stored
        const scrollY = document.body.getAttribute('data-scroll-y');
        const scrollX = document.body.getAttribute('data-scroll-x');
        
        document.body.classList.remove('preloader-active');
        document.documentElement.classList.remove('preloader-active');
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.classList.remove('preloader-active');
        }
        
        // Restore scroll position after a brief delay to ensure styles are removed
        if (scrollY || scrollX) {
          setTimeout(() => {
            window.scrollTo(parseInt(scrollX || '0', 10), parseInt(scrollY || '0', 10));
            document.body.removeAttribute('data-scroll-y');
            document.body.removeAttribute('data-scroll-x');
          }, 50);
        }
      }, 200); // Longer delay to ensure no scrollbars appear
    }, NORMAL_ANIMATION_DURATION); // Wait for full transition duration
  }, [setIsPreloading, NORMAL_ANIMATION_DURATION]);

  // Allow Enter/Space to trigger the PROCEED button when it's visible
  useEffect(() => {
    if (!showProceedButton || showRevealAnimation) {
      return;
    }

    const handleKeyDown = (event) => {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Space' ||
        event.key === 'Spacebar'
      ) {
        // Prevent conflicts with inputs (shouldn't exist on preloader, but guard anyway)
        if (
          event.target?.tagName === 'INPUT' ||
          event.target?.tagName === 'TEXTAREA' ||
          event.target?.isContentEditable
        ) {
          return;
        }

        event.preventDefault();
        handleProceedClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showProceedButton, showRevealAnimation, handleProceedClick]);


  return (
    <>
      {/* Main preloader - outer container strictly constrained to viewport */}
      <div 
        data-preloader
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          zIndex: 10000,
          clipPath: 'inset(0)',
          contain: 'layout style paint',
          pointerEvents: 'auto', // Block all clicks except those explicitly allowed
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
      >
        {/* Inner container that handles the scale transform */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100vw',
            height: '100vh',
            transform: showRevealAnimation 
              ? 'translate(-50%, -50%) scale(20) translateZ(2000px)' 
              : 'translate(-50%, -50%) scale(1) translateZ(0px)',
            transformOrigin: 'center center',
            background: showRevealAnimation 
              ? 'radial-gradient(circle at center, #FF6600 0%, #FF4400 30%, #FF2200 60%, #CC1100 100%)'
              : '#FF2200',
            opacity: showRevealAnimation ? 0 : (debugPreloadRef.current && !showProceedButton ? 0.3 : 1),
            filter: showRevealAnimation 
              ? 'blur(8px) brightness(2)' 
              : 'blur(0px) brightness(1)',
            transition: showRevealAnimation ? `all ${NORMAL_ANIMATION_DURATION / 1000}s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
            perspective: '2000px',
            willChange: showRevealAnimation ? 'transform, opacity, filter' : 'auto',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontFamily: 'Inter, sans-serif',
            padding: '0 2rem',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            pointerEvents: 'none' // Allow clicks to pass through to button container
          }}
          ref={preloaderRef}
        >
        {/* Hide message when PROCEED button is shown */}
        {!showProceedButton && (
          <div style={{
            fontSize: '1rem',
            fontFamily: '"Pixelify Sans", system-ui',
            fontOpticalSizing: 'auto',
            fontWeight: 400,
            fontStyle: 'normal',
            letterSpacing: '0.05em',
            marginBottom: '1rem',
            opacity: showRevealAnimation ? 0.3 : 0.8,
            transform: showRevealAnimation 
              ? 'scale(0.85) translateZ(-80px)' 
              : 'translateZ(0)',
            transition: showRevealAnimation ? `all ${NORMAL_ANIMATION_DURATION / 1000}s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
            transitionDelay: showRevealAnimation ? '0.2s' : '0s',
            textAlign: 'center',
            maxWidth: '90%',
            lineHeight: '1.4',
            minHeight: '2.8rem', // Fixed height to accommodate 2 lines (1rem * 1.4 * 2)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}>
            {loadingStatus}
          </div>
        )}
        
        {/* Custom progress bar - hide when PROCEED button is shown */}
        {!showProceedButton && (
          <div style={{
            width: '300px',
            height: '6px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            overflow: 'hidden',
            position: 'relative',
            marginBottom: '1rem',
            transform: showRevealAnimation 
              ? 'scale(0.9) translateZ(-60px)' 
              : 'scale(1) translateZ(0px)',
            opacity: showRevealAnimation ? 0.5 : 1,
            transition: showRevealAnimation ? `all ${NORMAL_ANIMATION_DURATION / 1000}s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
            transitionDelay: showRevealAnimation ? '0.3s' : '0s'
          }}>
            <div style={{
              width: `${displayedProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #ffffff 0%, #f0f0f0 100%)',
              borderRadius: '3px',
              transition: 'none', // Remove CSS transition since we're handling animation in JS
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Shimmer effect */}
              <div 
                className="shimmer-effect"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.12), transparent)',
                  mixBlendMode: 'multiply',
                  transform: 'translateX(-100%)',
                  animation: displayedProgress === 100 ? 'none' : 'shimmerX 1.6s linear infinite'
                }} 
              />
            </div>
          </div>
        )}
        
        {/* Progress percentage - hide when PROCEED button is shown */}
        {!showProceedButton && (
          <div style={{
            fontSize: '1.2rem',
            fontFamily: '"Pixelify Sans", system-ui',
            fontOpticalSizing: 'auto',
            fontWeight: 400,
            fontStyle: 'normal',
            letterSpacing: '0.05em',
            lineHeight: '1.2',
            color: 'white',
            opacity: showRevealAnimation ? 0.4 : 1,
            transform: showRevealAnimation 
              ? 'scale(0.88) translateZ(-40px)' 
              : 'translateZ(0)',
            transition: showRevealAnimation ? `all ${NORMAL_ANIMATION_DURATION / 1000}s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
            transitionDelay: showRevealAnimation ? '0.4s' : '0s',
            textAlign: 'center',
            height: '1.5rem', // Fixed height for consistent spacing
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}>
            {isFinite(displayedProgress) ? Math.round(displayedProgress) : 0}%
          </div>
        )}
        
        {/* PROCEED Button */}
        {showProceedButton && !showRevealAnimation && (
          <div 
            className="button-container"
            data-preloader-button="true"
            style={{
              position: 'relative',
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: showButtonAnimation ? 1 : 0,
              transform: showButtonAnimation 
                ? 'scale(1) translateY(0px)' 
                : 'scale(0.8) translateY(20px)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
              pointerEvents: 'auto'
            }}
          >
            <VintageButton 
              onClick={handleProceedClick}
              visible={showButtonAnimation}
            >
              enter new work city
            </VintageButton>
          </div>
        )}
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes shimmerX {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default Preloader;
